import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Paper, Stack, Group, Text, ActionIcon, Badge, Loader, Center, ThemeIcon, Tooltip, Button } from '@mantine/core'
import { IoChevronBackOutline, IoTimeOutline, IoCheckmarkDoneOutline } from 'react-icons/io5'
import { formatDistanceToNow } from 'date-fns'
import type { BuildPartDto } from '../../app/models/build'
import agent from '../../app/api/agent'

interface VersionHistoryItem {
  versionNumber: number
  changeDescription: string
  userId: string
  userName: string
  createdAt: string
  isCollaboratorEdit: boolean
  partsSnapshot: string
}

interface Props {
  buildId: string | undefined
  isOwner: boolean
  isOpen: boolean
  onClose: () => void
  onRestoreVersion?: (versionNumber: number, parts: BuildPartDto[]) => Promise<void>
}

export default observer(function BuildHistorySidebar ({
  buildId,
  isOwner,
  isOpen,
  onClose,
  onRestoreVersion
}: Props) {
  const [history, setHistory] = useState<VersionHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null)
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen || !buildId) return

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await agent.Builds.getVersionHistory(buildId)
        if (response.isSuccess) {
          setHistory(response.value ?? [])
        } else {
          setError(response.error ?? 'Failed to load history')
        }
      } catch (err) {
        setError('Failed to load version history')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [isOpen, buildId])

  const handleRestore = async (versionNumber: number, partsSnapshot: string) => {
    if (!onRestoreVersion) return

    try {
      setRestoringVersion(versionNumber)
      const parts: BuildPartDto[] = JSON.parse(partsSnapshot)
      await onRestoreVersion(versionNumber, parts)
      setExpandedVersion(null)
    } catch (err) {
      setError('Failed to restore version')
    } finally {
      setRestoringVersion(null)
    }
  }

  if (!isOpen) return null

  return (
    <Paper
      withBorder
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: 'clamp(320px, 35vw, 480px)',
        background: 'var(--gs-surface)',
        zIndex: 100,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-2px 0 12px rgba(0,0,0,0.15)',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      {/* Header */}
      <Group
        position="apart"
        align="center"
        p="md"
        sx={{ borderBottom: '1px solid var(--gs-border)', flexShrink: 0 }}
      >
        <Group spacing={8}>
          <IoTimeOutline size={20} style={{ color: 'var(--gs-accent)' }} />
          <Text weight={600} size="lg">Version History</Text>
        </Group>
        <ActionIcon variant="light" onClick={onClose}>
          <IoChevronBackOutline size={18} />
        </ActionIcon>
      </Group>

      {/* Content */}
      <Stack spacing="xs" p="md" sx={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <Center py={40}>
            <Loader size="sm" />
          </Center>
        )}

        {error && (
          <Paper p="sm" bg="red" sx={{ borderRadius: 6 }}>
            <Text size="sm" color="white">{error}</Text>
          </Paper>
        )}

        {!loading && !error && history.length === 0 && (
          <Center py={40}>
            <Text size="sm" color="dimmed">No version history yet</Text>
          </Center>
        )}

        {!loading && history.map((version, idx) => (
          <Paper
            key={version.versionNumber}
            p="sm"
            withBorder
            sx={{
              borderColor: expandedVersion === version.versionNumber ? 'var(--gs-accent)' : 'var(--gs-border)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'var(--gs-accent)',
                bg: 'var(--gs-surface2)'
              }
            }}
            onClick={() => setExpandedVersion(
              expandedVersion === version.versionNumber ? null : version.versionNumber
            )}
          >
            {/* Version Header */}
            <Group position="apart" align="flex-start" noWrap mb="xs">
              <div style={{ flex: 1 }}>
                <Group spacing={6} mb={4}>
                  <Badge size="sm" variant="filled" color="blue">
                    v{version.versionNumber}
                  </Badge>
                  {version.isCollaboratorEdit && (
                    <Badge size="sm" variant="outline" color="orange">
                      Collab
                    </Badge>
                  )}
                </Group>
                <Text size="xs" color="dimmed">
                  {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                </Text>
              </div>
              <ThemeIcon
                size="sm"
                variant="light"
                color="blue"
                radius="md"
                sx={{ flexShrink: 0 }}
              >
                <IoCheckmarkDoneOutline size={14} />
              </ThemeIcon>
            </Group>

            {/* Change Description */}
            <Text size="sm" weight={500} sx={{ color: 'var(--gs-text)', lineHeight: 1.4 }}>
              {version.changeDescription}
            </Text>

            {/* Editor Info */}
            <Text size="xs" color="dimmed" mt="xs">
              by <strong>{version.userName}</strong>
            </Text>

            {/* Expanded Details */}
            {expandedVersion === version.versionNumber && (
              <Stack spacing="sm" mt="md" pt="md" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                <div>
                  <Text size="xs" weight={600} mb={4}>Parts in this version:</Text>
                  <Text size="xs" color="dimmed">
                    {(() => {
                      try {
                        const parts = JSON.parse(version.partsSnapshot) as any[]
                        return `${parts.length} part${parts.length === 1 ? '' : 's'}`
                      } catch {
                        return 'Unable to parse'
                      }
                    })()}
                  </Text>
                </div>

                {isOwner && onRestoreVersion && (
                  <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    loading={restoringVersion === version.versionNumber}
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleRestore(version.versionNumber, version.partsSnapshot)
                    }}
                    fullWidth
                  >
                    Restore to this version
                  </Button>
                )}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </Paper>
  )
})
